// ===============================================================
// ProBank — Landing Page
//
// Sticky banking background image (public/hero-bg.jpg) with a
// dark overlay so text always stays readable. The video stays
// fixed while content scrolls over it.
//
// Copy is written in plain, human language. No fake stats, no
// countdown claims, no popup hijack.
//
// Includes the PWA install prompt: visible on every visit until
// the visitor installs the app, then it never shows again.
// ===============================================================

import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useCountry } from "../context/CountryContext";
import InstallAppPrompt from "../components/InstallAppPrompt";

const TELEGRAM_BOT_URL = "https://t.me/probanksupport_bot";

export default function LandingPage() {
  const navigate = useNavigate();
  const { isGhana, resetCountry } = useCountry();

  const theme = isGhana
    ? {
        appName: "ProBank Ghana",
        country: "Ghana",
        flagAccent: "GH",
      }
    : {
        appName: "ProBank",
        country: "Nigeria",
        flagAccent: "NG",
      };

  const handleSwitchCountry = () => {
    resetCountry();
    navigate("/");
  };

  const features = [
    {
      title: "Instant transaction alerts",
      desc: "The moment money moves in or out of your account, your phone tells you. With sound, so you never miss it.",
    },
    {
      title: "A virtual card that works",
      desc: "Apply for a ProBank virtual card and use it to pay on websites and apps, just like the card in your pocket.",
    },
    {
      title: "Simple transfers",
      desc: "Send money by bank transfer, manage your wallets, and see every transaction in one place.",
    },
    {
      title: "Real people on support",
      desc: "Every account is reviewed by our team. If you ever need help, you talk to a person, not a robot.",
    },
  ];

  const steps = [
    {
      n: "1",
      title: "Create your account",
      desc: "Register in a few minutes. Pick the plan that fits how you bank.",
    },
    {
      n: "2",
      title: "Fund your wallet",
      desc: "Pay online or by bank transfer. Once we confirm it, your account is ready.",
    },
    {
      n: "3",
      title: "You are good to go",
      desc: "Make transfers, get alerts, and manage everything from your dashboard.",
    },
  ];

  return (
    <div
      className="min-h-screen text-[#EDEAE3]"
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
      }}
    >
      {/* ================= Sticky background ================= */}
      <div className="fixed inset-0 -z-10 overflow-hidden bg-[#0B0D10]">
        <img
          src="/hero-bg.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B0D10]/[0.82]" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 900px 500px at 15% 0%, rgba(184,147,95,0.10), transparent 60%)",
          }}
        />
      </div>

      {/* ================= Header ================= */}
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between border-b border-white/[0.08]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#14171C]/80 border border-[#B8935F]/30 flex items-center justify-center">
            <img src="/banklogo.png" alt="ProBank" className="w-6 h-6 opacity-90" />
          </div>
          <div>
            <div className="text-base font-bold tracking-tight">{theme.appName}</div>
            <div className="text-[11px] text-[#9B9DA3]">{theme.country}</div>
          </div>
        </div>

        <nav className="flex items-center gap-2 text-sm">
          <button
            onClick={handleSwitchCountry}
            className="text-[10px] font-semibold tracking-widest px-3 py-2 rounded border border-white/15 text-[#9B9DA3] hover:border-white/30 hover:text-[#EDEAE3] transition"
          >
            {theme.flagAccent}
          </button>
          <Link
            to="/login"
            className="hidden sm:inline-block px-4 py-2.5 text-[#D5D2C9] hover:text-[#EDEAE3] transition"
          >
            Log in
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center px-5 py-2.5 rounded bg-[#B8935F] text-[#0B0D10] font-semibold hover:bg-[#C7A472] transition"
          >
            Open an account
          </Link>
        </nav>
      </header>

      {/* ================= Hero ================= */}
      <main className="max-w-6xl mx-auto px-6 pt-20 pb-24">
        <div className="max-w-2xl">
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl font-bold leading-[1.15] tracking-tight"
          >
            Banking that keeps you{" "}
            <span className="text-[#C7A472]">in the know.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-[#D5D2C9] text-base leading-relaxed"
          >
            ProBank alerts you the moment money moves in or out of your
            account. You can send transfers, get a virtual card for online
            payments, and talk to a real person when you need help.
            Simple as that.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-9 flex items-center gap-4 flex-wrap"
          >
            <Link
              to="/register"
              className="inline-flex items-center px-7 py-3.5 bg-[#B8935F] text-[#0B0D10] font-semibold rounded hover:bg-[#C7A472] transition text-sm"
            >
              Open an account
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center px-6 py-3.5 border border-white/20 text-[#D5D2C9] hover:border-white/35 hover:text-white transition text-sm rounded"
            >
              Log in
            </Link>
          </motion.div>

          {/* ============ Install the app ============
              Always visible until the visitor installs ProBank.
              Once installed, it never appears again. */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            <InstallAppPrompt />
          </motion.div>

          {/* Trust line */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-[#9B9DA3]"
          >
            <span className="flex items-center gap-1.5">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Secure payments
            </span>
            <span className="flex items-center gap-1.5">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Accounts reviewed by our team
            </span>
            <span className="flex items-center gap-1.5">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
              Direct support on Telegram
            </span>
          </motion.div>
        </div>
      </main>

      {/* ================= How it works ================= */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-white/[0.08]">
        <h2 className="text-2xl font-bold tracking-tight mb-10">How it works</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-[#14171C]/[0.85] backdrop-blur-sm border border-white/[0.08] rounded-lg p-6"
            >
              <div className="w-9 h-9 rounded-full bg-[#B8935F]/[0.15] border border-[#B8935F]/30 flex items-center justify-center text-[#C7A472] font-bold text-sm mb-4">
                {s.n}
              </div>
              <h3 className="font-semibold text-[15px] mb-2">{s.title}</h3>
              <p className="text-[13px] text-[#9B9DA3] leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= Features ================= */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-white/[0.08]">
        <div className="mb-10 max-w-lg">
          <h2 className="text-2xl font-bold tracking-tight">
            What you get with ProBank
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-px bg-white/[0.08] rounded-lg overflow-hidden border border-white/[0.08]">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="bg-[#0B0D10]/[0.88] backdrop-blur-sm p-7"
            >
              <h3 className="font-semibold text-[15px] mb-2 text-[#EDEAE3]">
                {f.title}
              </h3>
              <p className="text-[13px] text-[#9B9DA3] leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-white/[0.08]">
        <div className="bg-[#14171C]/[0.85] backdrop-blur-sm border border-[#B8935F]/25 rounded-lg p-8 sm:p-10 text-center">
          <h2 className="text-2xl font-bold tracking-tight">
            Ready when you are.
          </h2>
          <p className="text-[#9B9DA3] text-sm mt-3 max-w-md mx-auto leading-relaxed">
            Registration takes a few minutes. And if you get stuck, our
            support team is one message away.
          </p>
          <div className="mt-7 flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/register"
              className="inline-flex items-center px-7 py-3.5 bg-[#B8935F] text-[#0B0D10] font-semibold rounded hover:bg-[#C7A472] transition text-sm"
            >
              Open an account
            </Link>
            <a
              href={TELEGRAM_BOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3.5 border border-white/20 text-[#D5D2C9] hover:border-white/35 hover:text-white transition text-sm rounded"
            >
              Speak with support
            </a>
          </div>
        </div>
      </section>

      {/* ================= Footer ================= */}
      <footer className="max-w-6xl mx-auto px-6 py-8 text-[13px] text-[#9B9DA3] flex flex-col md:flex-row items-center justify-between gap-3 border-t border-white/[0.08]">
        <div>
          <div className="text-[#D5D2C9] font-semibold">{theme.appName}</div>
          <div className="text-[11px] text-[#9B9DA3] mt-0.5">
            {isGhana ? "Serving Ghana" : "Serving Nigeria"}
          </div>
        </div>
        <div className="flex items-center gap-5">
          <a href="/terms" className="hover:text-[#EDEAE3] transition">Terms</a>
          <a href="/privacy" className="hover:text-[#EDEAE3] transition">Privacy</a>
          <a
            href={TELEGRAM_BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#EDEAE3] transition"
          >
            Support
          </a>
        </div>
      </footer>
    </div>
  );
}
